import { BrowserRouter, Route, Routes } from "react-router";
import { AppLayout, NotFoundPage } from "@/domains/general";
import { V2Router } from "@/v2";
import { DashboardPage } from "@/domains/dashboard";
import { LoginPage, RequireAuth, RequireRole } from "@/domains/auth";
import { AssetDetailPage, AssetListPage, AssetMapPage } from "@/domains/assets";
import {
  RequestDetailPage,
  RequestListPage,
  RequestWizard,
} from "@/domains/requests";
import { CaseDetailPage, CaseListPage } from "@/domains/cases";
import { FollowUpPage, TaskCalendarPage, TaskListPage } from "@/domains/tasks";
import { PlanningPage } from "@/domains/planning";
import { TemplateListPage } from "@/domains/communication";
import { ReportingPage } from "@/domains/reporting";
import {
  AssetTypesPage,
  ConfigurationPage,
  TaskTemplatesPage,
} from "@/domains/configuration";

function V1Router(): React.JSX.Element {
  return (
    <BrowserRouter basename="/v1">
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          element={
            <RequireAuth>
              <AppLayout />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardPage />} />

          <Route
            path="/requests"
            element={
              <RequireRole roles={["requester", "manager"]}>
                <RequestListPage />
              </RequireRole>
            }
          />
          <Route
            path="/requests/new"
            element={
              <RequireRole roles={["requester", "manager"]}>
                <RequestWizard />
              </RequireRole>
            }
          />
          <Route
            path="/requests/:id"
            element={
              <RequireRole roles={["requester", "manager"]}>
                <RequestDetailPage />
              </RequireRole>
            }
          />

          <Route
            path="/assets"
            element={
              <RequireRole roles={["manager"]}>
                <AssetListPage />
              </RequireRole>
            }
          />
          <Route
            path="/assets/map"
            element={
              <RequireRole roles={["manager"]}>
                <AssetMapPage />
              </RequireRole>
            }
          />
          <Route
            path="/assets/:id"
            element={
              <RequireRole roles={["manager"]}>
                <AssetDetailPage />
              </RequireRole>
            }
          />

          <Route
            path="/cases"
            element={
              <RequireRole roles={["manager"]}>
                <CaseListPage />
              </RequireRole>
            }
          />
          <Route
            path="/cases/:id"
            element={
              <RequireRole roles={["manager"]}>
                <CaseDetailPage />
              </RequireRole>
            }
          />

          <Route
            path="/tasks"
            element={
              <RequireRole roles={["manager", "partner"]}>
                <TaskCalendarPage />
              </RequireRole>
            }
          />
          <Route
            path="/tasks/list"
            element={
              <RequireRole roles={["manager", "partner"]}>
                <TaskListPage />
              </RequireRole>
            }
          />
          <Route
            path="/follow-up"
            element={
              <RequireRole roles={["requester"]}>
                <FollowUpPage />
              </RequireRole>
            }
          />

          <Route
            path="/planning"
            element={
              <RequireRole roles={["manager"]}>
                <PlanningPage />
              </RequireRole>
            }
          />

          <Route
            path="/configuration"
            element={
              <RequireRole roles={["manager"]}>
                <ConfigurationPage />
              </RequireRole>
            }
          />
          <Route
            path="/configuration/asset-types"
            element={
              <RequireRole roles={["manager"]}>
                <AssetTypesPage />
              </RequireRole>
            }
          />
          <Route
            path="/configuration/email-templates"
            element={
              <RequireRole roles={["manager"]}>
                <TemplateListPage />
              </RequireRole>
            }
          />
          <Route
            path="/configuration/task-templates"
            element={
              <RequireRole roles={["manager"]}>
                <TaskTemplatesPage />
              </RequireRole>
            }
          />
          <Route
            path="/communication/templates"
            element={
              <RequireRole roles={["manager"]}>
                <TemplateListPage />
              </RequireRole>
            }
          />

          <Route
            path="/reporting"
            element={
              <RequireRole roles={["manager"]}>
                <ReportingPage />
              </RequireRole>
            }
          />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

// Eén Vite-app, twee versies naast elkaar: paden onder "/v1" krijgen de
// volledige v1-app, al de rest is voorbehouden voor v2. De grens tussen beide
// is een full reload (aparte routers met een eigen basename).
function isV1Path(pathname: string): boolean {
  return pathname === "/v1" || pathname.startsWith("/v1/");
}

export function AppRouter(): React.JSX.Element {
  return isV1Path(window.location.pathname) ? <V1Router /> : <V2Router />;
}
